import type { MarketMonitorSourceScope } from './index';
import { HISTORY_REASONS } from './collectionHistory';

export const COMMENT_HISTORY_SUPPORT = 'reddit-comment-history/v1';
export const COMMENT_HISTORY_CAPABILITY = 'reddit.comments.collect_by_url';
const POSTS = 'reddit.posts.discover_by_keyword';
export interface CommentHistoryPolicy {
    schema_version: typeof COMMENT_HISTORY_SUPPORT;
    mode: 'adaptive_recheck';
    overlap_hours: number;
    max_days_back: number;
}
export interface CommentHistoryPreview {
    contract_version: 'market-comment-history-preview/v1';
    source_slice_id: string;
    input: string;
    initial_days_back: number;
    max_threads: number;
    max_comments_per_thread: number;
    planning_stage: 'after_thread_selection';
    coverage_semantics: 'observed_sample_only';
    policy: CommentHistoryPolicy;
}
export interface CommentHistoryPlan {
    contract_version: 'market-comment-history-plan/v1';
    capability: typeof COMMENT_HISTORY_CAPABILITY;
    evaluated_at: string;
    initial_days_back: number;
    max_days_back: number;
    overlap_hours: number;
    coverage_semantics: 'observed_sample_only';
    gap_codes: string[];
    checkpoint_job_id?: string;
    requested_since: string | null;
    slice_id: string;
    input: string;
    phase: 'initial_history' | 'incremental_recheck' | 'gap_recheck' | 'needs_review';
    action: 'collect' | 'confirm_scope';
    resolved_days_back?: number;
    reason_code?: typeof HISTORY_REASONS[number];
    dispatch_valid_until?: string;
    dispatched_at?: string;
}
const record = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
const integer = (value: unknown, min: number, max: number): value is number => Number.isSafeInteger(value) && Number(value) >= min && Number(value) <= max;
const date = (value: unknown): value is string => typeof value === 'string' && /(?:Z|[+-]\d{2}:\d{2})$/.test(value) && Number.isFinite(Date.parse(value));

export function normalizeCommentHistory(value: unknown, days: unknown): CommentHistoryPolicy | null {
    if (value == null) return null;
    if (!record(value) || Object.keys(value).length !== 4 || value.schema_version !== COMMENT_HISTORY_SUPPORT
        || value.mode !== 'adaptive_recheck' || !integer(days, 1, 30) || !integer(value.overlap_hours, 1, 168)
        || !integer(value.max_days_back, days, 30) || value.overlap_hours * 3600 + 300 >= value.max_days_back * 86400)
        throw new Error('Invalid comment history policy');
    return { schema_version: COMMENT_HISTORY_SUPPORT, mode: 'adaptive_recheck', overlap_hours: value.overlap_hours, max_days_back: value.max_days_back };
}
export function validCommentHistories(scopes: unknown): boolean {
    if (scopes === undefined) return true;
    if (!Array.isArray(scopes)) return false;
    try { return scopes.every(scope => record(scope) && (scope.comments == null || record(scope.comments)
        && (scope.comments.collection_history == null || scope.capability === POSTS && scope.source_system === 'reddit'
            && !!normalizeCommentHistory(scope.comments.collection_history, scope.comments.days_back)))); }
    catch { return false; }
}
export const needsCommentHistoryPreview = (scopes: MarketMonitorSourceScope[], previous: MarketMonitorSourceScope[] = []) =>
    scopes.some(scope => scope.comments?.collection_history !== undefined) || previous.some(scope => scope.comments?.collection_history != null);
const sameScope = (a: MarketMonitorSourceScope, b: MarketMonitorSourceScope) => a.capability === b.capability && a.source_system === b.source_system
    && a.scope.kind === b.scope.kind && a.scope.value === b.scope.value;
const effectiveComments = (scope: MarketMonitorSourceScope, previous: MarketMonitorSourceScope[]) => {
    const old = previous.find(item => sameScope(scope, item));
    return scope.comments && scope.comments.collection_history === undefined && old?.comments?.collection_history != null
        ? { ...scope.comments, collection_history: old.comments.collection_history } : scope.comments;
};
export function commentHistoryScopesMatch(requested: MarketMonitorSourceScope[], echoed: unknown, previous: MarketMonitorSourceScope[] = []): boolean {
    if (!needsCommentHistoryPreview(requested, previous)) return true;
    if (!Array.isArray(echoed) || !validCommentHistories(echoed)) return false;
    try {
        for (const scope of requested) {
            if (scope.capability !== POSTS) continue;
            const matches = (echoed as MarketMonitorSourceScope[]).filter(item => sameScope(scope, item));
            if (matches.length !== 1) return false;
            const wanted = effectiveComments(scope, previous), actual = matches[0].comments;
            if (!wanted) { if (actual != null) return false; continue; }
            if (!actual || actual.days_back !== wanted.days_back || actual.max_threads !== wanted.max_threads
                || actual.max_comments_per_thread !== wanted.max_comments_per_thread
                || JSON.stringify(normalizeCommentHistory(wanted.collection_history, wanted.days_back))
                    !== JSON.stringify(normalizeCommentHistory(actual.collection_history, actual.days_back))) return false;
        }
        return (echoed as MarketMonitorSourceScope[]).every(scope => scope.comments?.collection_history == null || requested.some(item => sameScope(item, scope)));
    } catch { return false; }
}
export function validCommentHistoryPreview(value: unknown): value is CommentHistoryPreview[] | undefined {
    if (value === undefined) return true;
    if (!Array.isArray(value)) return false;
    try { return value.every(item => record(item) && Object.keys(item).length === 9
        && item.contract_version === 'market-comment-history-preview/v1' && typeof item.source_slice_id === 'string' && !!item.source_slice_id
        && typeof item.input === 'string' && !!item.input.trim() && integer(item.initial_days_back, 1, 30)
        && integer(item.max_threads, 1, 10) && integer(item.max_comments_per_thread, 1, 500)
        && item.planning_stage === 'after_thread_selection' && item.coverage_semantics === 'observed_sample_only'
        && !!normalizeCommentHistory(item.policy, item.initial_days_back)); }
    catch { return false; }
}
export function commentHistoryPreviewMatches(requested: MarketMonitorSourceScope[], preview: {
    normalized_monitor: Record<string, unknown>; comment_history_support?: unknown; comment_history_preview?: unknown;
}, previous: MarketMonitorSourceScope[] = []): boolean {
    if (!needsCommentHistoryPreview(requested, previous)) return true;
    if (preview.comment_history_support !== COMMENT_HISTORY_SUPPORT || !commentHistoryScopesMatch(requested, preview.normalized_monitor.source_scopes, previous)
        || !validCommentHistoryPreview(preview.comment_history_preview)) return false;
    const enabled = requested.map(scope => effectiveComments(scope, previous)).filter(item => item?.collection_history != null);
    const descriptors = preview.comment_history_preview ?? [];
    if (!enabled.length) return descriptors.length === 0;
    const terms = preview.normalized_monitor.terms;
    if (!Array.isArray(terms) || !terms.every(term => record(term) && typeof term.value === 'string')) return false;
    const keywords = terms.filter(term => term.role !== 'exclude').map(term => term.value.trim().toLowerCase());
    return descriptors.length === keywords.length * enabled.length && new Set(descriptors.map(item => item.source_slice_id)).size === descriptors.length
        && keywords.every(keyword => descriptors.filter(item => item.input.trim().toLowerCase() === keyword).length === enabled.length)
        && descriptors.every(item => enabled.some(comments => item.initial_days_back === comments!.days_back
            && item.max_threads === comments!.max_threads && item.max_comments_per_thread === comments!.max_comments_per_thread
            && JSON.stringify(normalizeCommentHistory(item.policy, item.initial_days_back))
                === JSON.stringify(normalizeCommentHistory(comments!.collection_history, comments!.days_back))));
}
export function validCommentHistoryPlan(value: unknown): value is CommentHistoryPlan {
    if (!record(value) || value.contract_version !== 'market-comment-history-plan/v1' || value.capability !== COMMENT_HISTORY_CAPABILITY) return false;
    try {
        return !!normalizeCommentHistory({ schema_version: COMMENT_HISTORY_SUPPORT, mode: 'adaptive_recheck',
            overlap_hours: value.overlap_hours, max_days_back: value.max_days_back }, value.initial_days_back)
            && date(value.evaluated_at) && value.coverage_semantics === 'observed_sample_only'
            && typeof value.slice_id === 'string' && /^reddit-comments:[0-9a-f-]{36}$/.test(value.slice_id)
            && typeof value.input === 'string' && /^https:\/\/(?:www\.)?reddit\.com\//.test(value.input)
            && Array.isArray(value.gap_codes) && value.gap_codes.every(code => typeof code === 'string')
            && (value.requested_since === null || date(value.requested_since))
            && (value.dispatched_at === undefined || date(value.dispatched_at))
            && (value.checkpoint_job_id === undefined || typeof value.checkpoint_job_id === 'string')
            && (value.action === 'collect' ? ['initial_history', 'incremental_recheck', 'gap_recheck'].includes(String(value.phase))
                && integer(value.resolved_days_back, 1, Number(value.max_days_back)) && date(value.dispatch_valid_until)
                && (value.phase !== 'initial_history' || value.resolved_days_back === value.initial_days_back)
                : value.action === 'confirm_scope' && value.phase === 'needs_review'
                    && (value.resolved_days_back === undefined || integer(value.resolved_days_back, 1, Number(value.max_days_back)))
                    && HISTORY_REASONS.includes(value.reason_code as typeof HISTORY_REASONS[number]));
    } catch { return false; }
}
